import { useState } from 'react';
import { useSeoMeta } from '@unhead/react';
import { useNavigate } from 'react-router-dom';
import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import { nip19 } from 'nostr-tools';
import type { NostrEvent } from '@nostrify/nostrify';
import { useAuthor } from '@/hooks/useAuthor';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useEventById } from '@/hooks/useEventById';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useMuteList } from '@/hooks/useMuteList';
import { useFollowing } from '@/hooks/useFollowing';
import { useFollowMutation } from '@/hooks/useFollowMutation';
import { useChatAutoScroll } from '@/hooks/useChatAutoScroll';
import { getLivestreamInfo } from '@/lib/postUtils';
import { ChatMessage } from '@/components/feed/ChatMessage';
import { UnifiedVideoPlayer } from '@/components/UnifiedVideoPlayer';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent } from '@/components/ui/card';
import { Collapsible, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ArrowLeft, Wifi, Users, Send, MessageCircle, ExternalLink, PanelLeftClose, PanelLeft, UserPlus, UserCheck } from 'lucide-react';
import { Link } from 'react-router-dom';

interface LivestreamDetailPageProps {
  pubkey: string;
  dTag: string;
  /** Relay URLs from the NIP-19 identifier that are known to have this stream. */
  relays?: string[];
}

// ── Chat message list (shared markup for mobile + desktop panes) ─────────────

function ChatMessageList({
  isLoadingMessages,
  messages,
  isLive,
}: {
  isLoadingMessages: boolean;
  messages: NostrEvent[];
  isLive: boolean;
}) {
  if (isLoadingMessages) {
    return (
      <div className="space-y-1">
        {[1, 2, 3].map((i) => (
          <div key={i} className="py-1.5 px-1">
            <div className="flex gap-2">
              <Skeleton className="w-16 h-3 flex-shrink-0" />
              <Skeleton className="h-3 flex-1" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <p className="text-xs text-muted-foreground text-center py-6">
        {isLive ? 'No messages yet. Be the first to chat!' : 'No chat messages for this stream.'}
      </p>
    );
  }

  return (
    <>
      {messages.map((msg) => (
        <ChatMessage key={msg.id} event={msg} />
      ))}
    </>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────

export function LivestreamDetailPage({ pubkey, dTag, relays }: LivestreamDetailPageProps) {
  const { nostr } = useNostr();
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const { mutateAsync: publish, isPending: isSending } = useNostrPublish();
  const { data: followingList = [] } = useFollowing();
  const followMutation = useFollowMutation();
  const [chatMsg, setChatMsg] = useState('');
  const [isChatCollapsed, setIsChatCollapsed] = useState(false);

  // Fetch the livestream event, probing any relay hints first.
  const { data: event, isLoading } = useEventById({
    eventId: dTag,
    pubkey,
    kind: 30311,
    relayHints: relays,
    timeoutMs: 8000,
    staleTime: 5 * 1000,
    refetchInterval: 30 * 1000, // Re-fetch every 30s to get viewer count updates
  });

  const info = event ? getLivestreamInfo(event) : null;
  const hostPubkey = info?.hostPubkey ?? pubkey;
  const hostAuthor = useAuthor(hostPubkey);
  const hostMeta = hostAuthor.data?.metadata;
  const hostNpub = nip19.npubEncode(hostPubkey);
  const hostName = hostMeta?.display_name || hostMeta?.name || hostPubkey.slice(0, 10) + '…';
  const isFollowing = followingList.includes(hostPubkey);

  const isLive = info?.status === 'live';

  useSeoMeta({ title: info ? `${info.title} · Mixstr` : 'Livestream · Mixstr' });

  // The naddr coordinate used as the 'a' tag for live chat messages
  const aTagValue = `30311:${pubkey}:${dTag}`;

  // Get current user's blocklist
  const { isMuted: isUserMuted, isLoading: isUserBlocklistLoading } = useMuteList();

  // Fetch streamer's blocklist (kind 10000) to combine with user's blocklist
  const { data: streamerBlocklist = new Set<string>(), isLoading: isStreamerBlocklistLoading } = useQuery<Set<string>>({
    queryKey: ['nostr', 'streamer-blocklist', hostPubkey],
    queryFn: async ({ signal }) => {
      const [ev] = await nostr.query(
        [{ kinds: [10000], authors: [hostPubkey], limit: 1 }],
        { signal: AbortSignal.any([signal, AbortSignal.timeout(5000)]) },
      );
      if (!ev) return new Set<string>();
      const blocked = new Set<string>();
      for (const tag of ev.tags) {
        if (tag[0] === 'p' && tag[1]) blocked.add(tag[1]);
      }
      return blocked;
    },
    enabled: !!hostPubkey,
    staleTime: 2 * 60 * 1000,
  });

  // Fetch live chat messages (kind 1311 tagged with this stream's 'a' coordinate)
  const { data: chatMessages = [], isLoading: isChatLoading } = useQuery<NostrEvent[]>({
    queryKey: ['nostr', 'live-chat', aTagValue],
    queryFn: async ({ signal }) => {
      const events = await nostr.query(
        [{ kinds: [1311], '#a': [aTagValue], limit: 200 }],
        { signal: AbortSignal.any([signal, AbortSignal.timeout(8000)]) },
      );
      return events.sort((a, b) => a.created_at - b.created_at);
    },
    enabled: !!event,
    staleTime: 5 * 1000,
    refetchInterval: 10 * 1000,
  });

  // Combined filtering: hide messages from users blocked by either the viewer or the streamer
  const isBlockedByViewer = (event: NostrEvent) => isUserMuted(event);
  const isBlockedByStreamer = (event: NostrEvent) => streamerBlocklist.has(event.pubkey);
  const isBlocked = (event: NostrEvent) => isBlockedByViewer(event) || isBlockedByStreamer(event);

  // Wait for all blocklists to load before filtering
  const isBlocklistsLoading = isUserBlocklistLoading || isStreamerBlocklistLoading;
  const isLoadingMessages = isChatLoading || isBlocklistsLoading;
  const filteredChatMessages = isLoadingMessages ? [] : chatMessages.filter((msg) => !isBlocked(msg));

  // Identical auto-scroll behavior for both chat panes: stick to the bottom as
  // messages arrive, pause while hovered, and re-anchor 20s after the hover ends.
  const desktopChat = useChatAutoScroll(filteredChatMessages.length);
  const mobileChat = useChatAutoScroll(filteredChatMessages.length);

  async function handleSendChat() {
    if (!chatMsg.trim() || !event) return;
    try {
      await publish({
        kind: 1311,
        content: chatMsg.trim(),
        tags: [['a', aTagValue, '', 'root']],
      });
      setChatMsg('');
      // Jump straight to the newest message in whichever pane is visible.
      desktopChat.forceScrollToBottom();
      mobileChat.forceScrollToBottom();
    } catch {
      // ignore
    }
  }

  function handleChatKey(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendChat();
    }
  }

  function handleFollowToggle(e: React.MouseEvent) {
    e.preventDefault();
    if (!user) return;
    followMutation.mutate({ pubkey: hostPubkey, action: isFollowing ? 'unfollow' : 'follow' });
  }

  const chatCountLabel = isLoadingMessages
    ? 'Loading…'
    : `${filteredChatMessages.length} ${filteredChatMessages.length === 1 ? 'message' : 'messages'}`;

  return (
    // The page fills the main column exactly and never scrolls itself — only the
    // video/info area and the chat message lists scroll internally.
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-border bg-background/90 backdrop-blur px-4 py-3 flex items-center gap-3">
        <Button variant="ghost" size="icon" className="w-8 h-8 flex-shrink-0" onClick={() => navigate(-1)}>
          <ArrowLeft size={18} />
        </Button>
        <h1 className="text-base font-bold truncate flex-1">
          {isLoading ? 'Loading…' : (info?.title ?? 'Livestream')}
        </h1>
        {isLive && (
          <Badge className="bg-red-600 text-white border-0 gap-1 text-xs font-bold flex-shrink-0">
            <Wifi size={10} className="animate-pulse" />
            LIVE
          </Badge>
        )}
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4">
          <Skeleton className="w-full aspect-video rounded-xl" />
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-4 w-1/3" />
        </div>
      )}

      {/* Not found */}
      {!isLoading && !event && (
        <div className="flex-1 min-h-0 overflow-y-auto p-4">
          <Card className="border-dashed">
            <CardContent className="py-12 text-center text-muted-foreground text-sm">
              Stream not found or may have ended.
            </CardContent>
          </Card>
        </div>
      )}

      {event && info && (
        <div className="flex flex-1 min-h-0 flex-col lg:flex-row">
          {/* Main content — scrolls internally if it overflows */}
          <div className="flex-1 min-w-0 min-h-0 overflow-y-auto">
            <div className="px-4 pb-4">
              {/* Video player - full width */}
              <div className="relative aspect-video bg-black rounded-xl overflow-hidden shadow-lg">
                {info.streamUrl ? (
                  <UnifiedVideoPlayer
                    url={info.streamUrl}
                    title={info.title}
                    autoPlay={isLive}
                    isLive={isLive}
                    poster={info.thumbnail}
                  />
                ) : info.thumbnail ? (
                  <div className="relative w-full h-full">
                    <img
                      src={info.thumbnail}
                      alt={info.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/60">
                      <Wifi size={40} className={isLive ? 'text-red-400 animate-pulse' : 'text-muted-foreground'} />
                      <p className="text-white/80 text-sm">
                        {isLive ? 'Stream player not available in browser' : 'Stream has ended'}
                      </p>
                      {info.streamUrl && (
                        <a
                          href={info.streamUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1.5 text-xs text-primary hover:underline"
                        >
                          <ExternalLink size={12} />
                          Open stream directly
                        </a>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center gap-3 bg-gradient-to-br from-red-950 to-black">
                    <Wifi size={48} className={isLive ? 'text-red-500 animate-pulse' : 'text-muted-foreground'} />
                    <p className="text-white/60 text-sm">
                      {isLive ? 'No stream URL available' : 'Stream has ended'}
                    </p>
                  </div>
                )}
              </div>

              {/* Stream info/description */}
              <div className="py-4">
                <h2 className="text-lg font-bold text-foreground leading-snug">{info.title}</h2>

                <div className="flex items-center gap-3 mt-2 flex-wrap">
                  {/* Host */}
                  <Link to={`/${hostNpub}`} className="flex items-center gap-2 group">
                    <Avatar className="w-7 h-7">
                      <AvatarImage src={hostMeta?.picture} />
                      <AvatarFallback className="text-xs bg-primary/20 text-primary font-bold">
                        {hostName[0].toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-sm font-semibold group-hover:text-primary transition-colors">
                      {hostName}
                    </span>
                  </Link>

                  {/* Viewer count */}
                  {info.viewers != null && (
                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                      <Users size={13} />
                      {info.viewers.toLocaleString()} watching
                    </div>
                  )}

                  {/* Status */}
                  {info.status === 'planned' && (
                    <Badge className="bg-blue-600 text-white border-0 text-xs">Upcoming</Badge>
                  )}
                  {info.status === 'ended' && (
                    <Badge variant="secondary" className="text-xs">Ended</Badge>
                  )}

                  <Button
                    size="sm"
                    className="h-7 text-xs gap-1.5"
                    variant={isFollowing ? 'outline' : 'default'}
                    onClick={handleFollowToggle}
                    disabled={followMutation.isPending}
                  >
                    {followMutation.isPending ? (
                      <span className="animate-pulse">…</span>
                    ) : isFollowing ? (
                      <>
                        <UserCheck size={12} />
                        Following
                      </>
                    ) : (
                      <>
                        <UserPlus size={12} />
                        Follow
                      </>
                    )}
                  </Button>
                </div>

                {/* Summary */}
                {event.tags.find(([t]) => t === 'summary')?.[1] && (
                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                    {event.tags.find(([t]) => t === 'summary')![1]}
                  </p>
                )}

                {/* Hashtags */}
                {event.tags.filter(([t]) => t === 't').length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {event.tags.filter(([t]) => t === 't').map(([, tag]) => (
                      <Badge key={tag} variant="secondary" className="text-xs">
                        #{tag}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Desktop-only collapsable chat sidebar */}
          <Collapsible
            open={!isChatCollapsed}
            onOpenChange={(open) => setIsChatCollapsed(!open)}
            className="hidden lg:flex flex-col flex-shrink-0 min-h-0 border-l border-border bg-background shadow-lg transition-all duration-300 overflow-hidden"
            style={{ width: isChatCollapsed ? '3rem' : '20rem' }}
          >
            {/* Chat header with collapse toggle - always visible */}
            <div className="px-3 py-2.5 border-b border-border flex items-center gap-2 bg-card h-11 flex-shrink-0">
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="icon" className="h-5 w-5 flex-shrink-0 hover:bg-muted">
                  {isChatCollapsed ? (
                    <PanelLeft size={14} className="text-primary" />
                  ) : (
                    <PanelLeftClose size={14} className="text-primary" />
                  )}
                </Button>
              </CollapsibleTrigger>
              {!isChatCollapsed && (
                <>
                  <MessageCircle size={14} className="text-primary" />
                  <span className="text-sm font-semibold">Live Chat</span>
                  <span className="text-xs text-muted-foreground ml-auto">
                    {chatCountLabel}
                  </span>
                </>
              )}
            </div>

            {!isChatCollapsed && (
              <div className="flex-1 min-h-0 flex flex-col">
                {/* Messages - scrollable area */}
                <div
                  ref={desktopChat.setContainerRef}
                  {...desktopChat.containerProps}
                  className="flex-1 min-h-0 overflow-y-auto px-3 py-2"
                >
                  <ChatMessageList
                    isLoadingMessages={isLoadingMessages}
                    messages={filteredChatMessages}
                    isLive={isLive}
                  />
                </div>

                {/* Chat input - pinned to the bottom of the sidebar */}
                {isLive && (
                  <div className="px-3 py-2 border-t border-border flex gap-2 flex-shrink-0 bg-background">
                    {user ? (
                      <>
                        <Input
                          value={chatMsg}
                          onChange={(e) => setChatMsg(e.target.value)}
                          onKeyDown={handleChatKey}
                          placeholder="Say something…"
                          className="h-8 text-sm flex-1"
                          disabled={isSending}
                        />
                        <Button
                          size="icon"
                          className="h-8 w-8 flex-shrink-0 rounded-full"
                          onClick={handleSendChat}
                          disabled={!chatMsg.trim() || isSending}
                        >
                          <Send size={13} />
                        </Button>
                      </>
                    ) : (
                      <p className="text-xs text-muted-foreground py-1">
                        Log in to chat
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </Collapsible>

          {/* Mobile chat - pinned below the content, never pushes the page */}
          <div
            className="lg:hidden px-4 pb-4 min-h-0"
            style={{ height: 'clamp(300px, 40vh, 520px)' }}
          >
            <div className="h-full border rounded-xl overflow-hidden flex flex-col bg-background">
              {/* Chat header */}
              <div className="px-3 py-2.5 border-b border-border flex items-center gap-2 bg-card flex-shrink-0">
                <MessageCircle size={14} className="text-primary" />
                <span className="text-sm font-semibold">Live Chat</span>
                <span className="text-xs text-muted-foreground ml-auto">
                  {chatCountLabel}
                </span>
              </div>

              {/* Messages */}
              <div
                ref={mobileChat.setContainerRef}
                {...mobileChat.containerProps}
                className="flex-1 min-h-0 overflow-y-auto px-3 py-2"
              >
                <ChatMessageList
                  isLoadingMessages={isLoadingMessages}
                  messages={filteredChatMessages}
                  isLive={isLive}
                />
              </div>

              {/* Chat input */}
              {isLive && (
                <div className="px-3 py-2 border-t border-border flex gap-2 flex-shrink-0">
                  {user ? (
                    <>
                      <Input
                        value={chatMsg}
                        onChange={(e) => setChatMsg(e.target.value)}
                        onKeyDown={handleChatKey}
                        placeholder="Say something…"
                        className="h-8 text-sm flex-1"
                        disabled={isSending}
                      />
                      <Button
                        size="icon"
                        className="h-8 w-8 flex-shrink-0 rounded-full"
                        onClick={handleSendChat}
                        disabled={!chatMsg.trim() || isSending}
                      >
                        <Send size={13} />
                      </Button>
                    </>
                  ) : (
                    <p className="text-xs text-muted-foreground py-1">
                      Log in to chat
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
